import axios from 'axios';

import { ICustomer } from '@/shared/model/customer.model';
import { IDocument } from '@/shared/model/document.model';
import { SendOffer } from '@/shared/model/enumerations/send-offer.model';
import { ICustomerMap } from '@/shared/model/cusotmerMap.model';
import { ICustomerList } from '@/shared/model/customer-list.model';
import { IFullReport } from '@/shared/model/full-report.model';
import { IMarketer } from '@/shared/model/marketer.model';
import { ICustomerListEdit } from '@/shared/model/customer-list-edit.model';

const baseApiUrl = 'api/customers';
const baseAdminApiUrl = 'api/admin/customers';
const baseGroupApi = 'api/groups/available-customer';
import buildPaginationQueryOpts from '@/shared/sort/sorts';

export default class CustomerService {
  public find(id: number): Promise<ICustomer> {
    return new Promise<ICustomer>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          if (err.response.status === 404) {
            resolve(null);
          } else {
            reject(err);
          }
        });
    });
  }

  public customerCount(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/count`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          if (err.response.status === 404) {
            resolve(null);
          } else {
            reject(err);
          }
        });
    });
  }

  public retrieve(fromData: string, toDate: string, statusId: string, searchTerm: string, externalId: number, req?: object): Promise<any> {
    let url = `${baseApiUrl}?archived=false`;
    const params = new URLSearchParams();

    if (fromData) {
      params.append('fromDate', fromData);
    }
    if (externalId != null) {
      params.append('external', externalId.toString());
    }
    if (toDate) {
      params.append('toDate', toDate);
    }
    if (statusId) {
      params.append('status', statusId);
    }
    if (searchTerm) {
      params.append('query', searchTerm);
    }
    const queryString = params.toString();
    url += '&' + buildPaginationQueryOpts(req);
    if (queryString) {
      url += '&' + queryString;
    }
    return new Promise<any>((resolve, reject) => {
      axios
        .get(url)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveKanbanView(fromData: string, toDate: string, searchTerm: string, externalId: number): Promise<ICustomerList[]> {
    let url = baseApiUrl + '/kanban';
    const params = new URLSearchParams();

    if (fromData) {
      params.append('fromDate', fromData);
    }
    if (toDate) {
      params.append('toDate', toDate);
    }
    if (searchTerm) {
      params.append('query', searchTerm);
    }
    if (externalId != null) {
      params.append('external', externalId.toString());
    }

    const queryString = params.toString();
    if (queryString) {
      url += '?' + queryString;
    }
    return new Promise<ICustomerList[]>((resolve, reject) => {
      axios
        .get(url)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAdminsCustomers(): Promise<ICustomerList[]> {
    return new Promise<ICustomerList[]>((resolve, reject) => {
      axios
        .get(baseAdminApiUrl)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveLead(): Promise<ICustomerList[]> {
    return new Promise<ICustomerList[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/leads`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public changeCustomerStatus(customerId: number, statusId: number): Promise<ICustomerList[]> {
    return new Promise<ICustomerList[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/${customerId}/status/${statusId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public numOfLead(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/leads/count`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public update(entity: ICustomer): Promise<ICustomer> {
    return new Promise<ICustomer>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public updateCustomerList(entity: ICustomerListEdit): Promise<ICustomerListEdit> {
    return new Promise<ICustomerListEdit>((resolve, reject) => {
      axios
        .put(`${baseApiUrl}/list/${entity.id}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getCustomerListData(customerListId: number): Promise<ICustomerListEdit> {
    return new Promise<ICustomerListEdit>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/list/${customerListId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public suggest(text: string): Promise<ICustomer[]> {
    return new Promise<ICustomer[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/suggestion/${text}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public generateCapaFullPdf(id: number, reportData: IFullReport): Promise<IDocument> {
    return new Promise<IDocument>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/full-report/${id}`, reportData)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public sendDocumentToEmail(documentId: number, offerData: object): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/send-capafull-mail/${documentId}`, offerData, {
          headers: {
            'content-type': 'application/json',
          },
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getAllCustomerMapData(): Promise<ICustomerMap[]> {
    return new Promise<ICustomerMap[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/map`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public changeLeadStatus(customerId: number, active: boolean): Promise<ICustomer> {
    return new Promise<ICustomer>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/${customerId}/isLead/${active}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public confirmCustomer(customer: ICustomer): Promise<ICustomer> {
    return new Promise<ICustomer>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/confirm-address`, customer)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public getAvailableCustomer(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      axios
        .get(baseGroupApi)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public addMarketersToCustomer(customerId: number, marketerId: number): Promise<IMarketer[]> {
    return new Promise<IMarketer[]>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/marketers/${customerId}/${marketerId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public disableDirectCall(customerId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${baseApiUrl}/${customerId}/cancel-direct-call`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public deleteAccess(customerId: number, marketerId: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${baseApiUrl}/marketers/${customerId}/${marketerId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public retrieveAllCustomersMarketer(customerId: number): Promise<IMarketer[]> {
    return new Promise<IMarketer[]>((resolve, reject) => {
      axios
        .get(`${baseApiUrl}/external-marketers/${customerId}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
